import React from 'react';

import useEventTracking from '@hooks/useEventTracking';
import { article_path } from '@routes';
import { ClickTarget, ContentType, TrackingLocation, click } from '@tracking';
import { formatUtcDate } from '@utils/dates';

import { BlueprintAuthor, SectionItem } from './types';

const AuthorByline = ({
  authors,
  fallbackName,
}: {
  authors?: BlueprintAuthor[];
  fallbackName?: string | null;
}) => {
  if (authors && authors.length > 0) {
    return (
      <span className="font-suisse-screen text-byline-sm text-black">
        By{' '}
        {authors.map((author, i) => (
          <React.Fragment key={author.id || author.name}>
            {i > 0 && (i === authors.length - 1 ? ' and ' : ', ')}
            {author.name}
          </React.Fragment>
        ))}
      </span>
    );
  }

  if (fallbackName) {
    return <span className="font-suisse-screen text-byline-sm text-black">By {fallbackName}</span>;
  }

  return null;
};

interface BlueprintArticleCardProps {
  item: SectionItem;
}

const BlueprintArticleCard = ({ item }: BlueprintArticleCardProps) => {
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.Articles,
    contentId: item.contentId || item.id,
    contentType: ContentType.Article,
  });

  const slug = item.metadata?.slug;
  const href = slug ? article_path(slug) : '#';

  return (
    <a
      href={href}
      className="group flex flex-col no-underline"
      onClickCapture={() =>
        trackEvent(
          click({
            clickTarget: ClickTarget.Card,
            clickText: item.title || undefined,
            contentId: item.contentId || item.id,
            contentType: ContentType.Article,
          })
        )
      }
    >
      {item.imageUrl && (
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-grayscale-beige">
          <img
            src={item.imageUrl}
            alt={item.title || ''}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      )}
      <div className="mt-16 flex flex-col gap-[3px]">
        {item.label && (
          <p className="m-0 font-suisse-intl-mono text-overline-md text-primary-magenta">
            {item.label}
          </p>
        )}
        <div className="flex flex-col gap-[3px]">
          {item.title && (
            <h3 className="m-0 font-suisse-works text-headline-xs text-black">{item.title}</h3>
          )}
          {item.description && (
            <p className="m-0 font-suisse-neue text-body-md text-black">{item.description}</p>
          )}
          <div className="flex items-end gap-12">
            <AuthorByline authors={item.authors} fallbackName={item.authorName} />
            {item.publishedDate && (
              <span className="font-suisse-screen text-disclosure-sm text-grayscale-gray">
                {formatUtcDate(item.publishedDate)}
              </span>
            )}
          </div>
        </div>
      </div>
    </a>
  );
};

export default BlueprintArticleCard;
